import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import BookCard from "@/components/BookCard";
import { Button } from "@/components/ui/button";
import { Package, Loader2, Sparkles, Check, Crown, GraduationCap, Users, ArrowRight, Gem, Flame, Layers } from "lucide-react";
import { Link } from "react-router-dom";

const Library = () => {
  const [books, setBooks] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [certificationPathway, setCertificationPathway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [interestForm, setInterestForm] = useState({ name: "", email: "", background: "", intention: "" });
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [interestSubmitted, setInterestSubmitted] = useState(false);
  
  // Objects interest form state
  const [showObjectsForm, setShowObjectsForm] = useState(false);
  const [objectsForm, setObjectsForm] = useState({ name: "", email: "" });
  const [objectsSubmitting, setObjectsSubmitting] = useState(false);
  const [objectsSubmitted, setObjectsSubmitted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [booksRes, bundlesRes, pathwayRes] = await Promise.all([
          axios.get(`${API}/books/`),
          axios.get(`${API}/books/bundles`),
          axios.get(`${API}/certification/pathway`)
        ]);
        setBooks(booksRes.data);
        setBundles(bundlesRes.data);
        setCertificationPathway(pathwayRes.data);
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInterestSubmit = async (e) => {
    e.preventDefault();
    setInterestSubmitting(true);
    try {
      await axios.post(`${API}/certification/interest`, interestForm);
      setInterestSubmitted(true);
      setShowInterestForm(false);
    } catch (error) {
      console.error("Failed to submit interest", error);
    } finally {
      setInterestSubmitting(false);
    }
  };

  const handleObjectsInterest = async (e) => {
    e.preventDefault();
    setObjectsSubmitting(true);
    try {
      await axios.post(`${API}/objects/interest`, objectsForm);
      setObjectsSubmitted(true);
      setShowObjectsForm(false);
    } catch (error) {
      console.error("Failed to submit objects interest", error);
    } finally {
      setObjectsSubmitting(false);
    }
  };

  // Find the practitioner manual
  const practitionerManual = books.find(b => b.id === "practitioner-manual");
  
  // Find the starter book (Eight Pillars)
  const starterBook = books.find(b => b.is_starter);

  const levels = [
    { id: "all", label: "All Books" },
    { id: "entry", label: "Entry" },
    { id: "intermediate", label: "Intermediate" },
    { id: "advanced", label: "Advanced" },
    { id: "practitioner", label: "Practitioner" },
  ];

  const filteredBooks = filter === "all" 
    ? books 
    : books.filter(b => b.reading_level === filter);

  // Separate premium and regular bundles
  const premiumBundle = bundles.find(b => b.is_premium);
  const regularBundles = bundles.filter(b => !b.is_premium);

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="library-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Collection</span>
          <h1 className="font-heading text-5xl md:text-6xl text-[#EAEAEA] mb-4">
            Somalume Library
          </h1>
          <p className="text-[#A1A1AA] max-w-xl">
            Eleven volumes mapping the architecture of human coherence.
            From foundation to practitioner.
          </p>
        </div>

        {/* Start Here - Featured Entry Point */}
        {starterBook && (
          <div className="mb-16" data-testid="start-here-section">
            <div className="relative p-8 md:p-10 border border-gold/30 rounded-sm bg-gradient-to-br from-gold/5 via-transparent to-gold/10 overflow-hidden">
              <div className="absolute top-4 right-4 bg-gold text-[#050505] text-xs font-medium px-3 py-1 rounded-sm uppercase tracking-wider">
                {starterBook.badge || "Start Here"}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                <div className="md:col-span-2">
                  <span className="text-gold text-xs uppercase tracking-widest font-body mb-2 block">New to Somalume?</span>
                  <h2 className="font-heading text-2xl md:text-3xl text-[#EAEAEA] mb-2">
                    {starterBook.title}
                  </h2>
                  <p className="text-gold/70 text-sm mb-4">{starterBook.subtitle}</p>
                  <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                    {starterBook.short_description}
                  </p>
                  
                  <div className="flex items-center gap-6">
                    <div className="flex items-baseline gap-2">
                      {starterBook.price === 0 ? (
                        <span className="font-mono text-3xl text-gold uppercase tracking-widest">Free</span>
                      ) : (
                        <>
                          <span className="font-mono text-3xl text-gold">${starterBook.price.toFixed(0)}</span>
                          <span className="text-[#A1A1AA] text-sm">AUD</span>
                        </>
                      )}
                    </div>
                    <Link to={`/book/${starterBook.id}`}>
                      <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-11 px-8 uppercase text-xs tracking-widest">
                        Begin Here
                        <ArrowRight size={14} className="ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
                
                <div className="hidden md:block">
                  <div className="text-center text-[#A1A1AA] text-sm space-y-3">
                    <p className="border-b border-white/10 pb-3">Stillness · Movement · Rhythm</p>
                    <p className="border-b border-white/10 pb-3">Geometry · Pressure · Coherence</p>
                    <p>Radiance · Recursion</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Premium Archive - Featured */}
        {premiumBundle && (
          <div className="mb-20" data-testid="premium-section">
            <div className="relative p-8 md:p-12 border-2 border-gold/40 rounded-sm bg-gradient-to-br from-gold/10 via-gold/5 to-transparent overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gold/5 rounded-full blur-2xl" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <Crown size={24} className="text-gold" />
                  <span className="text-gold text-xs uppercase tracking-widest font-body">Premium Offering</span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <h2 className="font-heading text-3xl md:text-4xl text-[#EAEAEA] mb-4">
                      {premiumBundle.name}
                    </h2>
                    <p className="text-[#A1A1AA] mb-6">
                      {premiumBundle.description}
                    </p>
                    
                    <div className="flex items-baseline gap-4 mb-6">
                      <span className="font-mono text-4xl text-gold">${premiumBundle.price.toFixed(0)}</span>
                      {premiumBundle.savings > 0 && (
                        <>
                          <span className="font-mono text-lg text-[#A1A1AA] line-through">${premiumBundle.original_price.toFixed(0)}</span>
                          <span className="bg-green-500/20 text-green-400 text-xs uppercase px-3 py-1 rounded-sm">
                            Save ${premiumBundle.savings.toFixed(0)}
                          </span>
                        </>
                      )}
                    </div>
                    
                    <Button 
                      className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine"
                      data-testid="premium-cta"
                    >
                      <Sparkles size={16} className="mr-2" />
                      Get the Archive
                    </Button>
                  </div>
                  
                  <div className="bg-void/50 p-6 rounded-sm border border-white/5">
                    <h3 className="font-heading text-lg text-[#EAEAEA] mb-4">What's Included:</h3>
                    <ul className="space-y-3">
                      {premiumBundle.includes?.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                          <Check size={16} className="text-gold mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Practitioner Pathway Section */}
        {(practitionerManual || certificationPathway) && (
          <div className="mb-20" data-testid="practitioner-pathway-section">
            <div className="border border-gold/30 rounded-sm bg-gradient-to-br from-gold/5 via-transparent to-gold/10 overflow-hidden">
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <GraduationCap size={28} className="text-gold" />
                  <h2 className="font-heading text-2xl md:text-3xl text-[#EAEAEA]">
                    The Practitioner Pathway
                  </h2>
                </div>
                
                <p className="text-[#A1A1AA] max-w-2xl mb-10">
                  For coaches, healers, therapists, and facilitators called to work with human architecture. 
                  This isn't a book — it's training infrastructure.
                </p>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Practitioner Manual */}
                  {practitionerManual && (
                    <div className="bg-void/50 border border-white/10 rounded-sm p-6 relative">
                      <span className="absolute top-4 right-4 bg-gold/20 text-gold text-xs px-3 py-1 rounded-full uppercase tracking-wider">
                        Professional
                      </span>
                      <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">
                        {practitionerManual.title}
                      </h3>
                      <p className="text-gold/80 text-sm mb-4">
                        {practitionerManual.subtitle}
                      </p>
                      <p className="text-[#A1A1AA] text-sm mb-6 line-clamp-3">
                        {practitionerManual.short_description}
                      </p>
                      <div className="flex items-baseline gap-2 mb-6">
                        <span className="font-mono text-3xl text-gold">${practitionerManual.price?.toFixed(0)}</span>
                        <span className="text-[#A1A1AA] text-sm">AUD</span>
                      </div>
                      <ul className="space-y-2 mb-6 text-sm text-[#A1A1AA]">
                        <li className="flex items-center gap-2">
                          <Check size={14} className="text-gold" />
                          72 interconnected modules
                        </li>
                        <li className="flex items-center gap-2">
                          <Check size={14} className="text-gold" />
                          Session protocols & frameworks
                        </li>
                        <li className="flex items-center gap-2">
                          <Check size={14} className="text-gold" />
                          Diagnostic & intervention tools
                        </li>
                      </ul>
                      <Button 
                        className="w-full bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-12 uppercase text-xs tracking-widest"
                        data-testid="practitioner-manual-cta"
                      >
                        Get Practitioner Access
                        <ArrowRight size={14} className="ml-2" />
                      </Button>
                    </div>
                  )}

                  {/* Certification Pathway */}
                  {certificationPathway && (
                    <div className="bg-void/50 border border-gold/30 rounded-sm p-6 relative">
                      <span className="absolute top-4 right-4 bg-gold text-[#050505] text-xs px-3 py-1 rounded-full uppercase tracking-wider font-medium">
                        Coming Soon
                      </span>
                      <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">
                        {certificationPathway.title}
                      </h3>
                      <p className="text-gold/80 text-sm mb-4">
                        {certificationPathway.subtitle}
                      </p>
                      <p className="text-[#A1A1AA] text-sm mb-6">
                        {certificationPathway.description}
                      </p>
                      <div className="flex items-baseline gap-2 mb-6">
                        <span className="text-[#A1A1AA] text-sm">From</span>
                        <span className="font-mono text-3xl text-gold">${certificationPathway.estimated_price?.toFixed(0)}</span>
                        <span className="text-[#A1A1AA] text-sm">AUD</span>
                      </div>
                      <ul className="space-y-2 mb-6 text-sm text-[#A1A1AA]">
                        {certificationPathway.includes?.slice(0, 4).map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <Check size={14} className="text-gold" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      
                      {!interestSubmitted ? (
                        !showInterestForm ? (
                          <Button 
                            onClick={() => setShowInterestForm(true)}
                            className="w-full border border-gold/50 bg-transparent text-gold hover:bg-gold/10 rounded-none h-12 uppercase text-xs tracking-widest"
                            data-testid="certification-interest-cta"
                          >
                            <Users size={14} className="mr-2" />
                            Express Interest
                          </Button>
                        ) : (
                          <form onSubmit={handleInterestSubmit} className="space-y-3">
                            <input
                              type="text"
                              placeholder="Your name"
                              required
                              value={interestForm.name}
                              onChange={(e) => setInterestForm({...interestForm, name: e.target.value})}
                              className="w-full bg-void border border-white/10 rounded-sm px-3 py-2 text-sm text-[#EAEAEA] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-gold/50"
                            />
                            <input
                              type="email"
                              placeholder="Your email"
                              required
                              value={interestForm.email}
                              onChange={(e) => setInterestForm({...interestForm, email: e.target.value})}
                              className="w-full bg-void border border-white/10 rounded-sm px-3 py-2 text-sm text-[#EAEAEA] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-gold/50"
                            />
                            <input
                              type="text"
                              placeholder="Your background (optional)"
                              value={interestForm.background}
                              onChange={(e) => setInterestForm({...interestForm, background: e.target.value})}
                              className="w-full bg-void border border-white/10 rounded-sm px-3 py-2 text-sm text-[#EAEAEA] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-gold/50"
                            />
                            <Button 
                              type="submit"
                              disabled={interestSubmitting}
                              className="w-full bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-10 uppercase text-xs tracking-widest"
                            >
                              {interestSubmitting ? <Loader2 className="animate-spin" size={14} /> : "Submit Interest"}
                            </Button>
                          </form>
                        )
                      ) : (
                        <div className="text-center py-4 bg-gold/10 rounded-sm">
                          <Check size={20} className="text-gold mx-auto mb-2" />
                          <p className="text-gold text-sm">Interest registered. We'll be in touch.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Somalume Objects — Coming Soon */}
        <div className="mb-20" data-testid="objects-coming-soon-section">
          <div className="border border-white/10 rounded-sm bg-gradient-to-br from-white/5 via-transparent to-white/5 overflow-hidden">
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-2">
                <Gem size={24} className="text-gold" />
                <span className="text-gold text-xs uppercase tracking-widest">Coming Soon</span>
              </div>
              
              <h2 className="font-heading text-2xl md:text-3xl text-[#EAEAEA] mb-4">
                Somalume Objects
              </h2>
              
              <p className="text-[#A1A1AA] max-w-2xl mb-8">
                Physical artefacts designed to help the body remember coherence. 
                Not merchandise — materialized practice.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {/* Breath Candle */}
                <div className="bg-void/50 border border-white/5 rounded-sm p-5">
                  <Flame size={20} className="text-gold mb-3" />
                  <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Breath Candle</h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Timed burn for ritual practice. Three phases: Settle, Enter, Anchor.
                  </p>
                </div>

                {/* Somalume Stone */}
                <div className="bg-void/50 border border-white/5 rounded-sm p-5">
                  <Gem size={20} className="text-gold mb-3" />
                  <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Somalume Stone</h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Polished stone with thumb indentation. The nervous system listens to touch.
                  </p>
                </div>

                {/* Field Cards */}
                <div className="bg-void/50 border border-white/5 rounded-sm p-5">
                  <Layers size={20} className="text-gold mb-3" />
                  <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Field Cards</h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Observational prompts, not affirmations. Language that shapes perception.
                  </p>
                </div>
              </div>

              <div className="bg-void/30 border border-gold/20 rounded-sm p-6">
                <p className="text-[#EAEAEA] text-sm mb-4">
                  Be notified when Somalume Objects become available.
                </p>
                
                {!objectsSubmitted ? (
                  !showObjectsForm ? (
                    <Button 
                      onClick={() => setShowObjectsForm(true)}
                      className="border border-gold/50 bg-transparent text-gold hover:bg-gold/10 rounded-none h-11 px-8 uppercase text-xs tracking-widest"
                      data-testid="objects-interest-cta"
                    >
                      Notify Me
                    </Button>
                  ) : (
                    <form onSubmit={handleObjectsInterest} className="flex flex-col sm:flex-row gap-3 max-w-md">
                      <input
                        type="text"
                        placeholder="Your name"
                        required
                        value={objectsForm.name}
                        onChange={(e) => setObjectsForm({...objectsForm, name: e.target.value})}
                        className="flex-1 bg-void border border-white/10 rounded-sm px-3 py-2 text-sm text-[#EAEAEA] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-gold/50"
                      />
                      <input
                        type="email"
                        placeholder="Your email"
                        required
                        value={objectsForm.email}
                        onChange={(e) => setObjectsForm({...objectsForm, email: e.target.value})}
                        className="flex-1 bg-void border border-white/10 rounded-sm px-3 py-2 text-sm text-[#EAEAEA] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-gold/50"
                      />
                      <Button 
                        type="submit"
                        disabled={objectsSubmitting}
                        className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-10 px-6 uppercase text-xs tracking-widest"
                      >
                        {objectsSubmitting ? <Loader2 className="animate-spin" size={14} /> : "Submit"}
                      </Button>
                    </form>
                  )
                ) : (
                  <div className="flex items-center gap-2 text-gold">
                    <Check size={16} />
                    <span className="text-sm">You'll be notified when objects launch.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Regular Bundles */}
        <div className="mb-20" data-testid="bundles-section">
          <h2 className="font-heading text-2xl text-[#EAEAEA] mb-6 flex items-center gap-3">
            <Package size={24} className="text-gold" />
            Bundles & Collections
          </h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-gold" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {regularBundles.map((bundle) => (
                <div 
                  key={bundle.id} 
                  className="p-6 border border-gold/20 rounded-sm bg-gold/5 hover:bg-gold/10 transition-colors duration-300"
                  data-testid={`bundle-${bundle.id}`}
                >
                  <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">{bundle.name}</h3>
                  <p className="text-[#A1A1AA] text-sm mb-4">{bundle.description}</p>
                  <div className="flex items-baseline gap-3 mb-4 flex-wrap">
                    <span className="font-mono text-2xl text-gold">${bundle.price.toFixed(2)}</span>
                    {bundle.savings > 0 && (
                      <>
                        <span className="font-mono text-sm text-[#A1A1AA] line-through">${bundle.original_price.toFixed(2)}</span>
                        <span className="text-green-400 text-xs uppercase">Save ${bundle.savings.toFixed(2)}</span>
                      </>
                    )}
                  </div>
                  <p className="text-[#A1A1AA] text-xs mb-4">
                    Includes {bundle.book_ids.length} {bundle.book_ids.length === 1 ? "book" : "books"}
                    {bundle.id === "complete-library" && " (excludes Practitioner Manual)"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="filter-buttons">
          {levels.map((level) => (
            <Button
              key={level.id}
              variant={filter === level.id ? "default" : "outline"}
              onClick={() => setFilter(level.id)}
              className={`rounded-none text-xs uppercase tracking-widest ${
                filter === level.id 
                  ? "bg-gold text-[#050505] hover:bg-gold-accent" 
                  : "border-white/20 text-[#A1A1AA] hover:border-gold/30 hover:text-gold"
              }`}
              data-testid={`filter-${level.id}`}
            >
              {level.label}
            </Button>
          ))}
        </div>

        {/* Books Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-gold" size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="books-grid">
            {filteredBooks.map((book, index) => (
              <BookCard key={book.id} book={book} index={index} />
            ))}
          </div>
        )}

        {filteredBooks.length === 0 && !loading && (
          <div className="text-center py-20">
            <p className="text-[#A1A1AA]">No books found for this filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;
